// src/pages/SettingsTab.tsx
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getProjectSettings, updateProjectSettings, getProject, updateProject, triggerCrawl } from '../api/monitoring';
import { FiMail, FiRefreshCw, FiSave, FiGlobe, FiList, FiCode, FiPlus, FiTrash2, FiTag } from 'react-icons/fi';
import type { ProjectSettings } from '../api/types';
import { ScheduleSettings } from '../components/MonitoringProject/ScheduleSettings';
import { ScheduleReport } from '../components/MonitoringProject/ScheduleReport';
import { CustomHeadersEditor } from '../components/CustomHeadersEditor';
import styles from './SettingsTab.module.css';

import { logger } from '../utils/logger';

export default function SettingsTab() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  // ── Project name ────────────────────────────────────────────────────────────
  const [projectName, setProjectName] = useState<string>('');

  // ── Existing: email notifications ──────────────────────────────────────────
  const [notificationEmails, setNotificationEmails] = useState<string[]>([]);
  const [newNotificationEmail, setNewNotificationEmail] = useState<string>('');
  const [emailInputError, setEmailInputError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // ── New: Sitemap URLs ───────────────────────────────────────────────────────
  const [primarySitemapUrl, setPrimarySitemapUrl] = useState<string>('');
  const [additionalSitemapUrls, setAdditionalSitemapUrls] = useState<string[]>([]);
  // "saved" mirrors the last-committed value so we can diff for partial crawl
  const [savedAdditionalSitemaps, setSavedAdditionalSitemaps] = useState<string[]>([]);
  const [newSitemapInput, setNewSitemapInput] = useState<string>('');

  // ── New: Specific URLs ──────────────────────────────────────────────────────
  const [specificUrls, setSpecificUrls] = useState<string[]>([]);
  const [savedSpecificUrls, setSavedSpecificUrls] = useState<string[]>([]);
  const [newSpecificUrl, setNewSpecificUrl] = useState<string>('');

  // ── New: Custom headers ─────────────────────────────────────────────────────
  const [customHeaders, setCustomHeaders] = useState<Record<string, string>>({});

  // Fetch current settings (notification email, custom_headers, monitoring_scope…)
  // refetchOnWindowFocus disabled: window-focus refetches would overwrite unsaved local edits
  const { data: settingsData, isLoading: settingsLoading } = useQuery({
    queryKey: ['projectSettings', id],
    queryFn: () => getProjectSettings(id!),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  // Fetch project (needed for the primary sitemap_url scalar field)
  // refetchOnWindowFocus disabled: prevents primary sitemap field from resetting on tab-switch
  const { data: projectData, isLoading: projectLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => getProject(id!),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  // Sync form values from server data whenever it changes.
  // With refetchOnWindowFocus disabled, the only triggers are:
  //   1. Initial component mount
  //   2. After a mutation invalidates the query (post-save refetch)
  // Both are intentional, so we always accept the server state.
  useEffect(() => {
    if (settingsData) {
      logger.log('[SettingsTab] Server settings received:', JSON.stringify(settingsData.settings.monitoring_scope));
      // notification_emails (list) is preferred; fall back to the legacy
      // single-address field for projects saved before this UI existed.
      const emails = settingsData.settings.notification_emails
        ?? (settingsData.settings.notification_email ? [settingsData.settings.notification_email] : []);
      setNotificationEmails(emails);
      const sitemaps = settingsData.settings.additional_sitemaps || [];
      setAdditionalSitemapUrls(sitemaps);
      setSavedAdditionalSitemaps(sitemaps);          // mirror for diff
      const urls = settingsData.settings.monitoring_scope?.specific_urls || [];
      logger.log('[SettingsTab] Setting specificUrls to:', urls);
      setSpecificUrls(urls);
      setSavedSpecificUrls(urls);                    // mirror for diff
      setCustomHeaders(settingsData.settings.custom_headers || {});
    }
  }, [settingsData]);

  useEffect(() => {
    if (projectData) {
      setProjectName(projectData.name || '');
      setPrimarySitemapUrl(projectData.url || '');
    }
  }, [projectData]);

  // ── Mutations ───────────────────────────────────────────────────────────────
  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setErrorMessage(null);
    setTimeout(() => setSuccessMessage(null), 5000);
  };
  const showError = (msg: string) => {
    setErrorMessage(msg);
    setSuccessMessage(null);
  };

  // Rename project
  const saveNameMutation = useMutation({
    mutationFn: async () => {
      const trimmed = projectName.trim();
      if (!trimmed) throw new Error('Project name cannot be empty.');
      await updateProject(id!, { name: trimmed });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['monitoring-projects'] });
      showSuccess('Project name updated successfully!');
    },
    onError: (error: any) => showError(error.message || 'Failed to update project name'),
  });

  // Update notification/field settings
  const updateSettingsMutation = useMutation({
    mutationFn: async (updates: ProjectSettings) => {
      await updateProjectSettings(id!, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectSettings', id] });
      showSuccess('Email settings updated successfully!');
    },
    onError: (error: any) => showError(error.message || 'Failed to update settings'),
  });

  // Save sitemaps then crawl ONLY the newly added ones
  const saveSitemapsMutation = useMutation({
    mutationFn: async (): Promise<{ crawlStarted: boolean; newCount: number }> => {
      await updateProject(id!, { url: primarySitemapUrl || undefined, sitemap_urls: additionalSitemapUrls });

      // Diff: only crawl sitemaps the user just added
      const savedSet = new Set(savedAdditionalSitemaps);
      const newSitemaps = additionalSitemapUrls.filter((u) => !savedSet.has(u));

      if (newSitemaps.length === 0) {
        return { crawlStarted: false, newCount: 0 };
      }
      try {
        await triggerCrawl(id!, { sitemap_urls: newSitemaps });
        return { crawlStarted: true, newCount: newSitemaps.length };
      } catch {
        return { crawlStarted: false, newCount: newSitemaps.length };
      }
    },
    onSuccess: ({ crawlStarted, newCount }) => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['projectSettings', id] });
      // Update the saved mirror so the next save diffs correctly
      setSavedAdditionalSitemaps(additionalSitemapUrls);
      showSuccess(
        newCount === 0
          ? '✅ Sitemaps saved (no new sitemaps to crawl).'
          : crawlStarted
            ? `✅ Sitemaps saved & crawling ${newCount} new sitemap(s)! Pages will appear shortly.`
            : `✅ Sitemaps saved. Trigger a crawl from the project page to discover new pages.`,
      );
    },
    onError: (error: any) => showError(error.message || 'Failed to save sitemaps'),
  });

  // Save specific URLs then crawl ONLY the newly added ones
  const saveSpecificUrlsMutation = useMutation({
    mutationFn: async (): Promise<{ crawlStarted: boolean; newCount: number }> => {
      logger.log('[SettingsTab] Saving specific URLs:', specificUrls);
      await updateProject(id!, { specific_urls: specificUrls });

      // Diff: only crawl URLs the user just added
      const savedSet = new Set(savedSpecificUrls);
      const newUrls = specificUrls.filter((u) => !savedSet.has(u));

      if (newUrls.length === 0) {
        return { crawlStarted: false, newCount: 0 };
      }
      try {
        await triggerCrawl(id!, { urls: newUrls });
        return { crawlStarted: true, newCount: newUrls.length };
      } catch {
        return { crawlStarted: false, newCount: newUrls.length };
      }
    },
    onSuccess: ({ crawlStarted, newCount }) => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['projectSettings', id] });
      // Invalidate snapshots so removed URLs disappear immediately from the Snapshots tab
      queryClient.invalidateQueries({ queryKey: ['snapshots', id] });
      // Update the saved mirror
      setSavedSpecificUrls(specificUrls);
      showSuccess(
        newCount === 0
          ? '✅ URLs saved (no new URLs to crawl).'
          : crawlStarted
            ? `✅ URLs saved & crawling ${newCount} new URL(s)! They will appear shortly.`
            : `✅ URLs saved. Trigger a crawl from the project page to discover new pages.`,
      );
    },
    onError: (error: any) => showError(error.message || 'Failed to save URLs'),
  });

  // Save HTTP headers (no crawl needed)
  const saveHeadersMutation = useMutation({
    mutationFn: async () => {
      await updateProject(id!, { custom_headers: customHeaders });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['projectSettings', id] });
      showSuccess('HTTP headers saved successfully!');
    },
    onError: (error: any) => showError(error.message || 'Failed to save headers'),
  });

  // ── Handlers ────────────────────────────────────────────────────────────────
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleAddNotificationEmail = () => {
    const email = newNotificationEmail.trim();
    if (!email) return;
    if (!EMAIL_PATTERN.test(email)) {
      setEmailInputError(`"${email}" doesn't look like a valid email address.`);
      return;
    }
    if (notificationEmails.some((e) => e.toLowerCase() === email.toLowerCase())) {
      setEmailInputError('That email is already in the list.');
      return;
    }
    setEmailInputError(null);
    setNotificationEmails((prev) => [...prev, email]);
    setNewNotificationEmail('');
  };
  const handleRemoveNotificationEmail = (email: string) =>
    setNotificationEmails((prev) => prev.filter((e) => e !== email));
  const handleSaveEmail = () =>
    updateSettingsMutation.mutate({ notification_emails: notificationEmails });

  const handleAddSitemap = () => {
    const url = newSitemapInput.trim();
    if (!url || additionalSitemapUrls.includes(url)) return;
    setAdditionalSitemapUrls((prev) => [...prev, url]);
    setNewSitemapInput('');
  };
  const handleRemoveSitemap = (url: string) =>
    setAdditionalSitemapUrls((prev) => prev.filter((u) => u !== url));
  const handleSaveSitemaps = () => saveSitemapsMutation.mutate();

  const handleAddSpecificUrl = () => {
    const url = newSpecificUrl.trim();
    if (!url || specificUrls.includes(url)) return;
    setSpecificUrls((prev) => [...prev, url]);
    setNewSpecificUrl('');
  };
  const handleRemoveSpecificUrl = (url: string) =>
    setSpecificUrls((prev) => prev.filter((u) => u !== url));
  const handleSaveSpecificUrls = () => saveSpecificUrlsMutation.mutate();

  const handleSaveHeaders = () => saveHeadersMutation.mutate();
  const handleSaveName = () => saveNameMutation.mutate();

  // ── Derived state ───────────────────────────────────────────────────────────
  const isLoading = settingsLoading || projectLoading;
  const isSaving =
    saveNameMutation.isPending ||
    updateSettingsMutation.isPending ||
    saveSitemapsMutation.isPending ||
    saveSpecificUrlsMutation.isPending ||
    saveHeadersMutation.isPending;

  if (!id) {
    return (
      <div className={styles.errorState}>
        <p>Project ID not found</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <FiRefreshCw className={styles.loadingIcon} size={24} />
        <span>Loading settings...</span>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Project Settings</h2>
        <p className={styles.description}>
          Configure project name, crawl schedule, notifications, sitemaps, specific URLs, and HTTP headers for this monitoring project.
        </p>
      </div>

      {successMessage && (
        <div className={styles.successAlert}>
          <span className={styles.alertIcon}>✅</span>
          <span>{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className={styles.errorAlert}>
          <span className={styles.alertIcon}>❌</span>
          <span>{errorMessage}</span>
        </div>
      )}

      {/* ── Project Name ──────────────────────────────────────────────────── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <FiTag className={styles.sectionIcon} />
          <h3>Project Name</h3>
        </div>
        <p className={styles.sectionDescription}>
          Rename this monitoring job. The new name appears in the dashboard and detail header immediately.
        </p>

        <div className={styles.formGroup}>
          <label htmlFor="projectName" className={styles.label}>Name</label>
          <div className={styles.inputGroup}>
            <input
              id="projectName"
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
              placeholder="My monitoring project"
              className={styles.input}
              disabled={isSaving}
              maxLength={200}
            />
            <button
              onClick={handleSaveName}
              className={styles.saveButton}
              disabled={isSaving || !projectName.trim()}
            >
              {saveNameMutation.isPending ? (
                <>
                  <FiRefreshCw className={styles.spinIcon} size={16} />
                  Saving...
                </>
              ) : (
                <>
                  <FiSave size={16} />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Schedule Report - Shows next crawl time and status */}
      <ScheduleReport projectId={id} />

      {/* Crawl Schedule Section - with Tier-Based Scheduling */}
      <div className={styles.section}>
        <ScheduleSettings
          projectId={id}
          onSave={() => {
            setSuccessMessage('Schedule updated successfully!');
            setTimeout(() => setSuccessMessage(null), 5000);
          }}
        />
      </div>

      {/* Email Notifications Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <FiMail className={styles.sectionIcon} />
          <h3>Email Notifications</h3>
        </div>
        <p className={styles.sectionDescription}>
          Receive email alerts when changes are detected. Add one or more addresses; remove all to disable notifications.
        </p>

        <div className={styles.formGroup}>
          <label className={styles.label}>Notification Emails</label>
          {notificationEmails.length > 0 && (
            <ul className={styles.urlList}>
              {notificationEmails.map((email) => (
                <li key={email} className={styles.urlRow}>
                  <span className={styles.urlText} title={email}>{email}</span>
                  <button type="button" className={styles.removeBtn} onClick={() => handleRemoveNotificationEmail(email)} disabled={isSaving} title="Remove email">
                    <FiTrash2 size={13} />
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className={styles.addRow}>
            <input
              id="notificationEmail"
              type="email"
              value={newNotificationEmail}
              onChange={(e) => { setNewNotificationEmail(e.target.value); setEmailInputError(null); }}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddNotificationEmail())}
              placeholder="your-email@example.com"
              className={styles.input}
              disabled={isSaving}
            />
            <button type="button" onClick={handleAddNotificationEmail} className={styles.addBtn} disabled={isSaving || !newNotificationEmail.trim()} title="Add email">
              <FiPlus size={15} />
              Add
            </button>
          </div>
          {emailInputError && <p className={styles.errorAlert}>{emailInputError}</p>}
          <p className={styles.hint}>
            💡 Emails include the top 10 URLs with changes. Remove all addresses to disable notifications.
          </p>

          <button
            onClick={handleSaveEmail}
            className={styles.saveButton}
            disabled={isSaving}
          >
            {updateSettingsMutation.isPending ? (
              <>
                <FiRefreshCw className={styles.spinIcon} size={16} />
                Saving...
              </>
            ) : (
              <>
                <FiSave size={16} />
                Save
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Sitemap URLs ─────────────────────────────────────────────────── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <FiGlobe className={styles.sectionIcon} />
          <h3>Sitemap URLs</h3>
        </div>
        <p className={styles.sectionDescription}>
          Manage the sitemap XML URLs used to discover pages for monitoring.
        </p>

        <div className={styles.formGroup}>
          <label htmlFor="primarySitemapUrl" className={styles.label}>Primary Sitemap URL</label>
          <input
            id="primarySitemapUrl"
            type="url"
            value={primarySitemapUrl}
            onChange={(e) => setPrimarySitemapUrl(e.target.value)}
            placeholder="https://example.com/sitemap.xml"
            className={styles.input}
            disabled={isSaving}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Additional Sitemaps</label>
          {additionalSitemapUrls.length > 0 && (
            <ul className={styles.urlList}>
              {additionalSitemapUrls.map((url) => (
                <li key={url} className={styles.urlRow}>
                  <span className={styles.urlText} title={url}>{url}</span>
                  <button type="button" className={styles.removeBtn} onClick={() => handleRemoveSitemap(url)} disabled={isSaving} title="Remove sitemap">
                    <FiTrash2 size={13} />
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className={styles.addRow}>
            <input
              type="url"
              value={newSitemapInput}
              onChange={(e) => setNewSitemapInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddSitemap()}
              placeholder="https://example.com/sitemap-news.xml"
              className={styles.input}
              disabled={isSaving}
            />
            <button type="button" onClick={handleAddSitemap} className={styles.addBtn} disabled={isSaving || !newSitemapInput.trim()} title="Add sitemap">
              <FiPlus size={15} />
              Add
            </button>
          </div>
          <p className={styles.hint}>💡 Additional sitemaps are crawled alongside the primary one. URLs are deduplicated automatically.</p>
        </div>

        <button onClick={handleSaveSitemaps} className={styles.saveButton} disabled={isSaving}>
          {saveSitemapsMutation.isPending
            ? <><FiRefreshCw className={styles.spinIcon} size={16} /> Saving & crawling...</>
            : <><FiSave size={16} /> Save &amp; Crawl Now</>}
        </button>
      </div>

      {/* ── Specific URLs ─────────────────────────────────────────────────── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <FiList className={styles.sectionIcon} />
          <h3>Specific URLs to Monitor</h3>
        </div>
        <p className={styles.sectionDescription}>
          Add individual URLs to crawl on every run, even if they are not in any sitemap.
        </p>

        {specificUrls.length > 0 && (
          <ul className={styles.urlList}>
            {specificUrls.map((url) => (
              <li key={url} className={styles.urlRow}>
                <span className={styles.urlText} title={url}>{url}</span>
                <button type="button" className={styles.removeBtn} onClick={() => handleRemoveSpecificUrl(url)} disabled={isSaving} title="Remove URL">
                  <FiTrash2 size={13} />
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.addRow}>
          <input
            type="url"
            value={newSpecificUrl}
            onChange={(e) => setNewSpecificUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddSpecificUrl()}
            placeholder="https://example.com/important-page"
            className={styles.input}
            disabled={isSaving}
          />
          <button type="button" onClick={handleAddSpecificUrl} className={styles.addBtn} disabled={isSaving || !newSpecificUrl.trim()} title="Add URL">
            <FiPlus size={15} />
            Add
          </button>
        </div>
        <p className={styles.hint}>⚠️ These URLs will be crawled on the next scheduled run regardless of sitemap coverage.</p>

        <button onClick={handleSaveSpecificUrls} className={`${styles.saveButton} ${styles.saveButtonTop}`} disabled={isSaving}>
          {saveSpecificUrlsMutation.isPending
            ? <><FiRefreshCw className={styles.spinIcon} size={16} /> Saving & crawling...</>
            : <><FiSave size={16} /> Save &amp; Crawl Now</>}
        </button>
      </div>

      {/* ── HTTP Headers & User-Agent ─────────────────────────────────────── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <FiCode className={styles.sectionIcon} />
          <h3>HTTP Headers &amp; User-Agent</h3>
        </div>
        <p className={styles.sectionDescription}>
          Custom HTTP headers sent with every crawl request. Useful for bypassing bot detection or accessing restricted content.
        </p>

        <CustomHeadersEditor
          headers={customHeaders}
          onChange={setCustomHeaders}
          disabled={isSaving}
          showTemplates={true}
        />

        <button onClick={handleSaveHeaders} className={styles.saveButton} disabled={isSaving}>
          {saveHeadersMutation.isPending
            ? <><FiRefreshCw className={styles.spinIcon} size={16} /> Saving...</>
            : <><FiSave size={16} /> Save Headers</>}
        </button>
      </div>
    </div>
  );
}

